/**
 * matrixCodegen.ts — emit the NumPy program for a matrix build.
 *
 * The generated snippet is standalone (paste into a notebook or model.py):
 * labeled axis lists, a dense aligned matrix literal (one source line per
 * row, values right-padded per column, row id as a trailing comment — reads
 * like textbook notation), the input vector, and `b = M @ x`. Mirrored in
 * `backend/matrix.py::generate_numpy`; the tests pin exact output lines.
 */
import type { AxisLabel, MatrixBuild, MatrixKind } from './matrix';

const VAR_NAMES: Record<MatrixKind, string> = { incidence: 'B', adjacency: 'A', laplacian: 'L' };

const KIND_TITLES: Record<MatrixKind, string> = {
  incidence: 'incidence matrix (nodes × edges)',
  adjacency: 'adjacency matrix (nodes × nodes)',
  laplacian: 'Laplacian matrix (nodes × nodes, L = D − A)',
};

/** Shared numeric formatting: at most 6 decimals, trailing zeros stripped,
 * −0 normalized to 0, integral floats print bare (1, not 1.0). Same rules as
 * the Python mirror. */
export function formatNumber(v: number): string {
  const n = Number(v.toFixed(6));
  if (n === 0) return '0';
  return String(n);
}

export interface CodegenMeta {
  graphId: string;
  graphLabel?: string;
  /** Where x came from, e.g. `edge param 'cost' (missing → 0)` or `custom values`. */
  inputNote?: string;
  /** What the cells hold (param key or 'constant 1'), for the header. */
  cellsNote?: string;
}

function pyList(labels: AxisLabel[]): string {
  return `[${labels.map((l) => `"${l.id}"`).join(', ')}]`;
}

/** Col-label legend, only emitted for a matrix-only snippet (no input
 * vector) — otherwise the per-row and per-x-element comments already carry
 * every label. Multi-line: one id per line, not a 400-char wall. */
function labelLines(labels: AxisLabel[]): string[] | null {
  const pairs = labels.filter((l) => l.label !== l.id);
  if (pairs.length === 0) return null;
  if (pairs.length > 20) return [`# Col labels: ${pairs.length} columns have display labels different from their ids`];
  return ['# Col labels:', ...pairs.map((l) => `#   ${l.id} = "${l.label}"`)];
}

/** Column widths for the aligned literal: widest formatted value per column,
 * +1 so every column keeps a uniform sign slot (the textbook ` 1, -1` look). */
function columnWidths(cells: string[][]): number[] {
  const widths = cells.reduce<number[]>((w, row) => {
    row.forEach((c, j) => { w[j] = Math.max(w[j] ?? 0, c.length); });
    return w;
  }, []);
  return widths.map((w) => w + 1);
}

export function generateNumpy(build: MatrixBuild, input: number[] | null, meta: CodegenMeta): string {
  const name = VAR_NAMES[build.kind];
  const out: string[] = [];
  const graphName = meta.graphLabel && meta.graphLabel !== meta.graphId
    ? `"${meta.graphId}" (${meta.graphLabel})`
    : `"${meta.graphId}"`;

  out.push('# Generated by VSKI LAB — matrix codegen');
  out.push(`# Graph ${graphName} — ${KIND_TITLES[build.kind]}`);
  out.push(`# Shape: ${build.rows.length}×${build.cols.length} · input: ${build.cols.length} · result: ${build.rows.length} · rows: ${build.rowAxis}, cols: ${build.colAxis}`);
  if (meta.cellsNote) out.push(`# Cells: ${meta.cellsNote}`);
  out.push('import numpy as np');
  out.push('');
  out.push(`rows = ${pyList(build.rows)}  # ${build.rowAxis}`);
  out.push(`cols = ${pyList(build.cols)}  # ${build.colAxis}`);
  if (input === null && build.colAxis !== build.rowAxis) {
    const legend = labelLines(build.cols);
    if (legend) out.push(...legend);
  }
  out.push('');

  if (build.cells.length === 0 || build.cells[0].length === 0) {
    out.push(`${name} = np.zeros((${build.rows.length}, ${build.cols.length}))`);
  } else {
    const fmt = build.cells.map((row) => row.map(formatNumber));
    const widths = columnWidths(fmt);
    out.push(`${name} = np.array([`);
    fmt.forEach((row, i) => {
      const r = build.rows[i];
      const label = r.label !== r.id ? ` (${r.label})` : '';
      out.push(`    [${row.map((c, j) => c.padStart(widths[j])).join(', ')}],  # ${r.id}${label}`);
    });
    out.push('])');
  }

  if (input !== null) {
    out.push('');
    out.push(`# Input vector x — ${meta.inputNote ?? 'custom values'}`);
    out.push('x = np.array([');
    const w = Math.max(...input.map((v) => formatNumber(v).length)) + 1;
    input.forEach((v, j) => {
      const c = build.cols[j];
      const label = c.label !== c.id ? ` (${c.label})` : '';
      out.push(`    ${formatNumber(v).padStart(w)},  # ${c.id}${label}`);
    });
    out.push('])');
    out.push('');
    out.push(`b = ${name} @ x    # result — b[i] corresponds to rows[i]`);
  }
  return out.join('\n');
}
